import React, { useEffect, useState } from 'react';
import {
    List,
    ListItem,
    ListItemText,
    Collapse,
    alpha,
    useTheme,
} from '@material-ui/core';
import { TMenuNode } from './types';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'react-feather';
import { makeStyles } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { MenuItemWrapper } from './MenuItemWrapper';
import { Label } from 'src/components/Tags';
import useAuth from 'src/hooks/useAuth';
import { hasActiveChild, getColorIntensity, canSeeItem } from '../utils';

const useStyles = makeStyles((theme) => ({
    list: {
        marginBottom: 10,
        marginTop: 5,
        paddingBottom: 5,
        paddingTop: 2,
    },
    icon: {
        display: 'flex',
        alignItems: 'center',
        transition: 'transform 0.25s ease',
        color: theme.palette.secondary.main,
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        borderRadius: 3,
        padding: theme.spacing(1),
        transition:
            'background-color 0.3s ease, color 0.3s ease, padding 0.2s ease',
    },
    active: {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        transition: 'background-color 0.3s ease, color 0.3s ease',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.075),
            color: '#FFF',
        },
        color: '#FFF',
    },
    hasActiveChild: {
        backgroundColor: alpha(theme.palette.secondary.main, 0.95),
        '&:hover': {
            backgroundColor: alpha(theme.palette.secondary.main, 0.95),
            color: '#FFF',
        },
        color: '#FFF',
    },
}));

/**
 * Represents the nested menu for the dashboard layout.
 *
 * @param {{
 *     items: TMenuNode[];
 *     level?: number;
 * }} {
 *     items,
 *     level = 1,
 * }
 * @return {*}
 */
export const NestedMenu = ({
    items,
    level = 1,
}: {
    items: TMenuNode[];
    level?: number;
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const theme = useTheme();
    const [open, setOpen] = useState<{ [key: string]: boolean }>({});
    const location = useLocation();
    const { user } = useAuth();

    const handleToggle = (key: string) => {
        setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    useEffect(() => {
        const newOpen: Record<string, boolean> = {};

        items.forEach((item) => {
            if (
                'items' in item &&
                hasActiveChild({ item, pathname: location.pathname })
            ) {
                newOpen[item.title] = true;
            }
        });

        setOpen(newOpen);
    }, [location.pathname, items]);

    return (
        <List className={classes.list}>
            {items
                .filter((item) =>
                    canSeeItem({
                        item,
                        permissions: user?.permissions,
                        accessLevel: user?.accessLevel,
                    })
                )
                .map((item, index) => {
                    const isLeaf = 'path' in item;
                    const hasChildren = 'items' in item;
                    const Icon = 'Icon' in item ? item.Icon : null;
                    const isActive = isLeaf
                        ? location.pathname === item.path
                        : false;
                    const activeChild = hasActiveChild({
                        item,
                        pathname: location.pathname,
                    });

                    return (
                        <div
                            key={index}
                            style={{
                                borderBottom:
                                    level === 1
                                        ? `1px solid ${theme.palette.divider}`
                                        : 'none',
                            }}
                        >
                            <MenuItemWrapper
                                hasTooltip={'tooltip' in item}
                                tooltip={
                                    'tooltip' in item ? item.tooltip : undefined
                                }
                                isLeaf={isLeaf}
                                path={isLeaf ? item.path : undefined}
                            >
                                <ListItem
                                    button
                                    onClick={() =>
                                        hasChildren && handleToggle(item.title)
                                    }
                                    className={clsx(classes.listItem, {
                                        [classes.hasActiveChild]:
                                            activeChild && !isLeaf,
                                        [classes.active]: isActive,
                                    })}
                                    style={{
                                        paddingLeft: level * 15,
                                        backgroundColor: activeChild
                                            ? alpha(
                                                  theme.palette.secondary.main,
                                                  getColorIntensity(level)
                                              )
                                            : 'transparent',
                                        color:
                                            isActive || activeChild
                                                ? '#FFF'
                                                : isLeaf
                                                  ? theme.palette.text.secondary
                                                  : theme.palette.text.primary,
                                    }}
                                >
                                    {Icon && (
                                        <Icon
                                            size={18}
                                            className={classes.icon}
                                            style={{
                                                color: activeChild
                                                    ? '#FFF'
                                                    : theme.palette.secondary
                                                          .main,
                                            }}
                                        />
                                    )}

                                    <ListItemText
                                        primary={t(item.title)}
                                        primaryTypographyProps={{
                                            style: {
                                                letterSpacing: 0.5,
                                                fontSize: 18,
                                            },
                                        }}
                                    />

                                    {'label' in item && item.label && (
                                        <Label
                                            color={
                                                isActive ? 'white' : 'primary'
                                            }
                                        >
                                            <p>{t(item.label)}</p>
                                        </Label>
                                    )}

                                    {hasChildren && (
                                        <ChevronRight
                                            style={{
                                                width: 18,
                                                transform: open[item.title]
                                                    ? 'rotate(90deg)'
                                                    : 'rotate(0deg)',
                                                transition:
                                                    'transform 0.25s ease',
                                            }}
                                        />
                                    )}
                                </ListItem>
                            </MenuItemWrapper>

                            {hasChildren && (
                                <Collapse
                                    in={open[item.title]}
                                    timeout={{ enter: 300, exit: 200 }}
                                    unmountOnExit
                                >
                                    <List component="div" disablePadding>
                                        <NestedMenu
                                            items={item.items}
                                            level={level + 1}
                                        />
                                    </List>
                                </Collapse>
                            )}
                        </div>
                    );
                })}
        </List>
    );
};
